import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import img from "../../constant";
import SubmitButton from "../../components/elements/forms/SubmitButton";
import FAQAccordion from "../../components/Accordion/Faq";
import { subscriptionService } from "../../services/api/settings";
import { Plans } from "../../store/types/settings";
import ModalNew from "../../components/Modal/ModalNew";
import { toast } from "sonner";

const Subscriptions: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<{ [key: number]: boolean }>({});
  const [authorizationUrl, setAuthorizationUrl] = useState<string>("");
  const [open, setOpen] = useState(false);

  const handleSubscription = async (item: Plans) => {
    console.log("Paystack clicked");
    if (item.tier === "STARTER" || loading[item.id]) return;

    setLoading((prev) => ({ ...prev, [item.id]: true }));

    try {
      const result = await subscriptionService.subscribe(item.tier); // default Paystack
      if (result.status === "success" && result.data?.authorizationUrl) {
        console.log("Paystack subscription successful:", result);
        setAuthorizationUrl(result.data.authorizationUrl);
        setOpen(true);
      } else {
        toast.error("Unable to initiate Paystack payment");
      }
    } catch (error) {
      console.error("Paystack subscription error:", error);
      // toast.error("Something went wrong while starting Paystack payment");
    } finally {
      setLoading((prev) => ({ ...prev, [item.id]: false }));
    }
  };

  const handlePaypalSubscription = async (item: Plans) => {
    console.log("PayPal clicked");
    if (item.tier === "STARTER" || loading[item.id]) return;

    setLoading((prev) => ({ ...prev, [item.id]: true }));

    try {
      const result = await subscriptionService.subscribe(item.tier, "paypal");
      if (result.status === "success" && result.data?.authorizationUrl) {
        window.open(result.data.authorizationUrl, "_blank");
      } else {
        toast.error("Unable to initiate PayPal payment");
      }
    } catch (error) {
      console.error("PayPal subscription error:", error);
      // toast.error("Something went wrong while starting PayPal payment");
    } finally {
      setLoading((prev) => ({ ...prev, [item.id]: false }));
    }
  };

  const handleModalClose = () => {
    setOpen(false);
    setAuthorizationUrl("");
  };

  return (
    <div className="px-3 py-10 md:py-18 bg-gradient-to-b from-[white-600]/20 to-[#4A94FB]/30 h-full">
      <div className="max-w-[1300px] mx-auto">
        <div className="text-center mb-10">
          <p className="text-[22px] md:text-[40px] font-semibold text-[#222222] mb-2">
            Flexible Pricing for Teams of all Sizes
          </p>
          <p className="text-[14px] md:text-[16px] text-[#4E4E4E]">
            From setup to hiring decisions—experience the complete AI-powered
            hiring transformation.
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 grid-cols-1 gap-3 w-full">
          {img?.simpleStep?.map((item) => (
            <div key={item.id} className="col-span-1 bshadow py-5">
              <div className="max-w-[200px] mx-auto px-2 text-center">
                <img src={item.icon} alt="25th" className="mx-auto" />
                <div className="h-[30.6px] w-[30.6px] mx-auto rounded-full bg-[#0B60D6] text-white text-[20px] md:text-[25px] flex items-center justify-center mt-5 mb-2">
                  {item.number}
                </div>
                <h2 className="max-w-[190px] mx-auto text-[20px] md:text-[37px] leading-[109.9%] text-[#222222] pt-3">
                  {item.title}
                </h2>
                <div className="flex items-center pt-3">
                  <img
                    src={item.icon2}
                    width={20}
                    height={20}
                    alt="25th"
                    className=""
                  />
                  <p className="text-[14px] md:text-[15px] text-[#4E4E4E] pl-2">
                    {item.text1}
                  </p>
                </div>
                <div className="flex items-center pt-2">
                  <img
                    src={item.icon2}
                    width={20}
                    height={20}
                    alt="25th"
                    className=""
                  />
                  <p className="text-[14px] md:text-[15px] text-[#4E4E4E] pl-2">
                    {item.text2}
                  </p>
                </div>

                {/* Show payment options only if not FREE */}
                {item.tier?.toUpperCase() !== "STARTER" && (
                  <div className="max-w-[180px] mx-auto mt-4 space-y-3">
                    <p className="text-sm font-semibold text-center text-[#333]">
                      Pay with Paystack or PayPal
                    </p>

                    {/* Paystack Button */}
                    {/* <SubmitButton
                      title="Pay with PayStack"
                      onSubmitHandler={() => handleSubscription(item)}
                      loading={loading[item.id] || false}
                      className="bg-primary text-white hover:bg-[#0056b3]"
                    /> */}

                    {/* PayPal Button */}
                    <SubmitButton
                      title="Pay with PayPal"
                      onSubmitHandler={() => handlePaypalSubscription(item)}
                      loading={loading[item.id] || false}
                      className="bg-primary text-white hover:bg-[#005ea6] hover:text-white"
                    />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Payment Modal for Paystack */}
        <ModalNew
          isOpen={open}
          onClose={handleModalClose}
          title="Complete Payment"
        >
          <div className="p-6">
            {authorizationUrl ? (
              <div className="space-y-4">
                <p className="text-gray-700 mb-4">
                  Please complete your payment to activate your subscription.
                </p>

                <div className="w-full h-96 border rounded-lg overflow-hidden">
                  <iframe
                    src={authorizationUrl}
                    className="w-full h-full"
                    title="Payment Gateway"
                    frameBorder="0"
                  />
                </div>

                <div className="flex flex-col sm:flex-row gap-3 pt-4">
                  <button
                    onClick={() => window.open(authorizationUrl, "_blank")}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded transition-colors"
                  >
                    Open Payment Page
                  </button>
                  <button
                    onClick={handleModalClose}
                    className="flex-1 bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p>Loading payment details...</p>
              </div>
            )}
          </div>
        </ModalNew>

        <div className="pt-10">
          <FAQAccordion />
        </div>
      </div>
    </div>
  );
};

export default Subscriptions;
